import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@table-library/react-table-library/theme'
import { getTheme } from '@table-library/react-table-library/baseline'
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from "@table-library/react-table-library/table"
import { updateJourneySelectedAddresses } from '@store/slice/pilotSlice'
import { updateActiveRowCall } from "@store/slice/sterilizationSlice"
import { useState } from 'react'
import useToggle from '@hook/useToggle'
import Buttons from '@ui/elements/Buttons'
import ScanPhotosModal from './ScanPhotosModal'
import Div from '@ui/layout/Div'
import TextType from '@ui/inputs/TextType'
import ActiveSterilizationCall from './ActiveSterilizationCall'

const SterilizationCallsListTable = () => {
  const [isPhotosModalActive, togglePhotosModal] = useToggle(false)
  const [modalPhotoFilter, setModalPhotoFilter] = useState(null)
  const [search, setSearch] = useState('')
  const [t] = useTranslation('common')
  const { calls, activeRowCall } = useSelector(state => state.sterilization)
  const { journeySelectedAddresses } = useSelector(state => state.pilot)
  const { mission } = useSelector(state => state.main)
  const dispatch = useDispatch()
  const theme = useTheme([getTheme(), {
    Table: `
        --data-table-library_grid-template-columns: 6% 15% 25% 30% repeat(3, minmax(0, 1fr));
      `,
  },])
  const resize = { resizerHighlight: "#dde2eb", resizerWidth: 5 }

  const getNodes = () => {
    const nodes = Object.values(calls).reverse()
    return nodes.filter(call => {
      const address = `${call.sterilizationDistrict}, ${call.sterilizationAddress},${call.sterilizationZipCode}`
      return call.missionResponsibleFullName.toLowerCase().includes(search.toLowerCase())
        || address.toLowerCase().includes(search.toLowerCase())
        || call.id == search
    })
  }

  const dataSterilizationCalls = { nodes: !calls ? [] : getNodes() }

  const activateRow = call => {
    dispatch(updateActiveRowCall(!activeRowCall || call.id !== activeRowCall.id ? call : null))
  }

  const addToJourneySelectedAddresses = call => {
    dispatch(updateJourneySelectedAddresses({ ...journeySelectedAddresses, [call.id]: call }))
  }

  const handleSearchField = (event) => {
    setSearch(event.target.value)
  }

  const removeFromJourneySelectedAddresses = callId => {
    const list = { ...journeySelectedAddresses }
    delete list[callId]
    dispatch(updateJourneySelectedAddresses({ ...list }))
  }

  const renderMissionSubjectsStat = (id, missionSubjectsStat) => {
    if (mission.missionSubjects.length > 1) {
      return mission.missionSubjects.map(missionSubject => {
        return (
          <h5 key={`${id}-${missionSubject.label}-stat`}>
            <span>{missionSubject.label}</span>: {missionSubjectsStat[missionSubject.label] || 0}
          </h5>
        )
      })
    }
  }

  return (
    <>
      <TextType value={search || ''} onChange={handleSearchField} placeholder={t('labels.search_field_sterilization_calls')} />
      <Table data={dataSterilizationCalls} theme={theme} layout={{ custom: true }} className='pilot-addresses'>
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell resize={resize}>ID</HeaderCell>
                <HeaderCell resize={resize}>{t('table.FullName')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Address')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Treatment')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Date')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.CallerType')}</HeaderCell>
                <HeaderCell resize={resize}>Actions</HeaderCell>
              </HeaderRow>
            </Header>
            <Body>
              {tableList.map(item =>
                <Row
                  key={item.id}
                  item={item}
                  onClick={() => activateRow(item)}
                  className={`pilot-address ${item.isSecondScanned ? 'is-second-scanned' : (item.isFirstScanned ? 'is-first-scanned' : '')}`}
                >
                  <Cell>#{item.id}</Cell>
                  <Cell>{item.missionResponsibleFullName}</Cell>
                  <Cell>
                    {item.companyName ? `${item.companyName},` : ''}{item.sterilizationDistrict}, {item.sterilizationAddress},{item.sterilizationZipCode}
                  </Cell>
                  <Cell className='scan-cell'>
                    <h4><span>{t('table.signaled_nests')}</span> :{item.nestsNumber}</h4>
                    <h3>{t('table.scan')}</h3>
                    <Div cssClass='columns'>
                      {item.scansData.isFirstScanned
                        ? <Div cssClass='column'>
                          <h4>{t('table.1st_scan')}{ }</h4>
                          <h5><span>{t('table.found_nests')}</span> :{item.scansData.firstScan.totalNests}</h5>
                          <h5><span>{t('table.empty_nests')}</span> :{item.scansData.firstScan.totalEmptyNests}</h5>
                          <h5><span>{t('table.nb_eggs')}</span> :{item.scansData.firstScan.totalEggs}</h5>
                          <h5><span>{t('table.nb_non_treated_eggs')}</span> :{item.scansData.firstScan.totalNonTreatedEggs}</h5>
                          <h5><span>{t('table.nb_birds')}</span> :{item.scansData.firstScan.totalBirds}</h5>
                          {renderMissionSubjectsStat(item.id, item.scansData.firstScan.missionSubjects)}
                        </Div>
                        : t('table.pending')
                      }
                      {item.scansData.isSecondScanned
                        ? <Div cssClass='column'>
                          <h4>{t('table.2nd_scan')}</h4>
                          <h5><span>{t('table.found_nests')}</span> :{item.scansData.secondScan.totalNests}</h5>
                          <h5><span>{t('table.empty_nests')}</span> :{item.scansData.secondScan.totalEmptyNests}</h5>
                          <h5><span>{t('table.nb_eggs')}</span> :{item.scansData.secondScan.totalEggs}</h5>
                          <h5><span>{t('table.nb_non_treated_eggs')}</span> :{item.scansData.secondScan.totalNonTreatedEggs}</h5>
                          <h5><span>{t('table.nb_birds')}</span> :{item.scansData.secondScan.totalBirds}</h5>
                          {renderMissionSubjectsStat(item.id, item.scansData.secondScan.missionSubjects)}
                        </Div>
                        : t('table.pending')
                      }
                    </Div>
                  </Cell>
                  <Cell>{item.createdAt}</Cell>
                  <Cell>{t(`table.${item.callType}`)}</Cell>
                  <Cell className='table-actions'>
                    <Buttons buttons={[
                      'undefined' === typeof journeySelectedAddresses[item.id]
                        ? {
                          icon: 'icon-location_off',
                          cssClass: 'is-small is-danger',
                          callback: () => addToJourneySelectedAddresses(item)
                        } : {
                          icon: 'icon-location_on',
                          cssClass: 'is-small is-success',
                          callback: () => removeFromJourneySelectedAddresses(item.id)
                        }
                    ]} />
                  </Cell>
                  {activeRowCall && activeRowCall.id === item.id
                    && <ActiveSterilizationCall
                      sterilizationCall={item}
                      togglePhotosModal={togglePhotosModal}
                      setModalPhotoFilter={setModalPhotoFilter}
                    />}
                </Row>
              )}
            </Body>
          </>
        )}
      </Table>

      {
        activeRowCall
          ? <ScanPhotosModal
            isModalActive={isPhotosModalActive}
            toggleModal={togglePhotosModal}
            photoFilter={modalPhotoFilter}
          />
          : <></>
      }
    </>
  )
}

export default SterilizationCallsListTable